import { useId, useState } from "react";
import { Button, Division, Form, FormInstance, Icon, Icons, Textarea, useToast } from "@ir/ui";
import { useAtom } from "jotai";
import { GenerateDisputeNotesDTO } from "@/core/dto";
import { Deduction } from "@/core/entities/deduction";
import { useWorkspace } from "@/hooks";
import { createLogger } from "@/infrastructure/services/logger";
import { generateDisputeNotesAtom } from "@/store/deduction/dispute";
import {
  RaiseDisputeDotfoodsFormValues,
  RaiseDisputeFormValues,
  RaiseDisputeUnfiFormValues,
} from "./RaiseDisputeInterface";

const logger = createLogger("DisputeNotesField");

const isPositiveAmount = (value: unknown): boolean => {
  if (value === null || value === undefined || value === "") {
    return false;
  }

  const amount = Number(value);
  return Number.isFinite(amount) && amount > 0;
};

const getDisputeTypeLabel = (value: unknown): string | null => {
  if (!value || typeof value !== "object") {
    return null;
  }

  const option = value as { label?: unknown; value?: unknown };
  if (typeof option.label !== "string" || option.label.trim().length === 0 || !option.value) {
    return null;
  }

  return option.label.trim();
};

const optionalText = (value: string | null | undefined): string | null => {
  if (typeof value !== "string" || value.trim().length === 0) {
    return null;
  }

  return value.trim();
};

type DisputeNotesFormInstance =
  | FormInstance<RaiseDisputeFormValues>
  | FormInstance<RaiseDisputeUnfiFormValues>
  | FormInstance<RaiseDisputeDotfoodsFormValues>;

interface DisputeNotesFieldProps {
  form: DisputeNotesFormInstance;
  deduction: Deduction;
  checkNumber: string;
  invoiceNumber: string;
  noteTemplate: string | null;
  fallbackDisputeAmount?: number | null;
}

const DisputeNotesSparkleIcon = () => {
  const rawId = useId().replace(/:/g, "");
  const gradientId = `dispute-notes-sparkle-gradient-${rawId}`;
  const clipId = `dispute-notes-sparkle-clip-${rawId}`;

  return (
    <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
      <g clipPath={`url(#${clipId})`}>
        <path
          d="M2.25 11V8.5M2.25 3.5V1M1 2.25H3.5M1 9.75H3.5M6.5 1.5L5.63291 3.75443C5.4919 4.12105 5.4214 4.30435 5.31176 4.45854C5.21459 4.5952 5.0952 4.71459 4.95854 4.81177C4.80435 4.9214 4.62105 4.99191 4.25443 5.13291L2 6L4.25443 6.86709C4.62105 7.00809 4.80435 7.0786 4.95854 7.18823C5.0952 7.28541 5.21459 7.4048 5.31177 7.54146C5.4214 7.69565 5.49191 7.87895 5.63291 8.24557L6.5 10.5L7.36709 8.24557C7.50809 7.87895 7.5786 7.69565 7.68824 7.54146C7.78541 7.4048 7.9048 7.28541 8.04146 7.18823C8.19565 7.0786 8.37895 7.00809 8.74557 6.86709L11 6L8.74557 5.13291C8.37895 4.99191 8.19565 4.9214 8.04146 4.81176C7.9048 4.71459 7.78541 4.5952 7.68823 4.45854C7.5786 4.30435 7.50809 4.12105 7.36709 3.75443L6.5 1.5Z"
          stroke={`url(#${gradientId})`}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <linearGradient
          id={gradientId}
          x1="0.999844"
          y1="10.9999"
          x2="10.9999"
          y2="0.999878"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#4300B1" />
          <stop offset="1" stopColor="#A531DC" />
        </linearGradient>
        <clipPath id={clipId}>
          <rect width="12" height="12" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};

const DisputeNotesField = ({
  form,
  deduction,
  checkNumber,
  invoiceNumber,
  noteTemplate,
  fallbackDisputeAmount,
}: DisputeNotesFieldProps) => {
  const notesForm = form as FormInstance<RaiseDisputeFormValues>;
  const { workspace } = useWorkspace();
  const { error: errorToast } = useToast();
  const [, generateDisputeNotes] = useAtom(generateDisputeNotesAtom);
  const [isGenerating, setIsGenerating] = useState(false);
  const disputeTypeValue = Form.useWatch("disputeType", notesForm);
  const disputeAmountValue = Form.useWatch("disputeAmount", notesForm);

  const showGenerateButton =
    getDisputeTypeLabel(disputeTypeValue) !== null &&
    (isPositiveAmount(disputeAmountValue) || isPositiveAmount(fallbackDisputeAmount));

  const resolveDisputeAmount = (): number | null => {
    if (isPositiveAmount(notesForm.getFieldValue("disputeAmount"))) {
      return Number(notesForm.getFieldValue("disputeAmount"));
    }

    if (isPositiveAmount(fallbackDisputeAmount)) {
      return Number(fallbackDisputeAmount);
    }

    return null;
  };

  const handleGenerate = async () => {
    const disputeType = getDisputeTypeLabel(notesForm.getFieldValue("disputeType"));
    const disputeAmount = resolveDisputeAmount();
    if (!disputeType || disputeAmount === null) {
      return;
    }

    setIsGenerating(true);
    try {
      const note = await generateDisputeNotes(
        new GenerateDisputeNotesDTO({
          checkNumber,
          invoiceNumber,
          deductionAmount: deduction.amount,
          purchaser: deduction.purchaser,
          disputeType,
          disputeAmount,
          noteTemplate,
          reasonCode: optionalText(deduction.reasonCode),
          rcaDescription: optionalText(deduction.rcaDescription),
          deductionType: optionalText(deduction.deductionType),
          poNumber: optionalText(deduction.poNumber),
          deductionDate: optionalText(deduction.deductionDate),
          invoiceDate: optionalText(deduction.invoiceDate),
          brandName: optionalText(workspace?.workspaceName),
        })
      );
      notesForm.setFieldValue("disputeRequestMessage", note);
    } catch (error) {
      logger.error("Failed to generate dispute notes", error);
      errorToast({
        content: "Unable to generate dispute notes. Please try again.",
        icon: <Icon name={Icons.AlertCircle} />,
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Division className="dispute-notes-field">
      <Form.Item
        name="disputeRequestMessage"
        rules={[
          { required: true, message: "Notes are required." },
          { max: 500, message: "Notes must be less than 500 characters." },
          { whitespace: true, message: "Notes cannot contain leading or trailing spaces." },
        ]}
        label="Notes"
      >
        <Textarea minRows={5} showCharCount maxLength={500} placeholder="Enter notes..." />
      </Form.Item>
      {(showGenerateButton || isGenerating) && (
        <Button
          type="button"
          variant="clear"
          size="small"
          className="dispute-notes-field__generate"
          htmlTitle={isGenerating ? "Generating dispute notes" : "Generate dispute notes"}
          aria-label={isGenerating ? "Generating dispute notes" : "Generate dispute notes"}
          isDisabled={isGenerating}
          onPress={handleGenerate}
        >
          {isGenerating ? <Icon name={Icons.Loading} className="icon-fast-loading" /> : <DisputeNotesSparkleIcon />}
        </Button>
      )}
    </Division>
  );
};

export default DisputeNotesField;
